/**
 * Single source of truth for every A/B experiment metric.
 *
 * The client experiment view (readiness clocks + setup forecast) and the staff
 * detail view (comparison table) render the same metric families through very
 * different layouts. They must NOT share a layout — but they must share the
 * *data contract*: each metric's label, its kind, and how to pull its numbers
 * out of an `ExperimentPooledRow`. Declaring that once here means a new metric
 * is added by editing this list plus the per-view key order arrays, never by
 * hand-editing parallel label maps and accessor blocks in each view (which is
 * how labels and CI fields silently drifted before).
 *
 * `ExperimentPooledRow` is the generated `get_experiment_pooled_results` RPC
 * return type; the accessors live here, next to it, so the two views stay in
 * lockstep.
 */
import type { Database } from '../types/database.generated';
import type { MetricClockKey } from './experimentMetricClocks';

export type ExperimentPooledRow =
    Database['public']['Functions']['get_experiment_pooled_results']['Returns'][number];

/**
 * Every metric the experiment views know about. Both views measure whole-session
 * `bounce` (`bounce_rate_*`) — the only bounce diff computable for an on/off
 * holdout, where control never shows Rose. Add future intent metrics
 * (`multi_page`, `return_visit_7d`) here once their columns land in the RPC.
 *
 * @deprecated `bounce_exposed` is retained for type compatibility only and is no
 * longer rendered by any view. `bounce_exposed_rate` keys off `widget_impression`
 * (saw_rose), which never fires in the control arm of *either* experiment type
 * (`staff` and `domain` both hold control out of Rose) — so its cross-arm diff is
 * always NULL and it can never be an A/B comparison. The backing mart columns
 * (`bounce_exposed_rate(_diff/_ci_*)`, `exposed_sessions`, `bounce_exposed_sessions`
 * in `analytics.refresh_experiment_results`) are likewise unused by the UI; drop
 * them in a follow-up mart migration if/when convenient. See IX-3471.
 */
export type ExperimentMetricKey =
    | 'conversion'
    | 'cta_click'
    | 'soft_conversion'
    | 'time_on_page'
    | 'bounce'
    | 'pricing'
    /** @deprecated unused by all views — see header note. */
    | 'bounce_exposed'
    | 'email_only'
    | 'session_p50'
    | 'session_p75';

/** How a metric's difference column is rendered (and read for significance). */
export type ExperimentMetricDiffKind =
    | 'rate-ci' // {diff} [{ciLow}, {ciHigh}]
    | 'duration-pvalue' // p={Mann-Whitney p}
    | 'informational'; // counts only, no comparison

export interface ExperimentMetricDescriptor {
    key: ExperimentMetricKey;
    /** Single source of truth for the on-screen label. */
    label: string;
    /** Drives the client setup-forecast sample sizing (proportion vs mean/SD). */
    kind: 'binary' | 'continuous';
    /** Inverse metric (e.g. bounce): a negative diff is the good outcome. */
    invert?: boolean;
    /** Setup-forecast baseline hint, shown in the client duration table. */
    hint?: string;
    /** Formatter selector for the per-variant value columns. */
    valueFormat: 'pct' | 'int' | 'duration';
    /** Per-variant value (control / treatment columns). */
    value: (r: ExperimentPooledRow) => number | null;
    diffKind: ExperimentMetricDiffKind;
    /** rate-ci accessors. */
    diff?: (r: ExperimentPooledRow) => number | null;
    ciLow?: (r: ExperimentPooledRow) => number | null;
    ciHigh?: (r: ExperimentPooledRow) => number | null;
    /** duration-pvalue accessor. */
    pValue?: (r: ExperimentPooledRow) => number | null;
    /** Client continuous-metric clock input (Mann-Whitney z). */
    z?: (r: ExperimentPooledRow) => number | null;
}

export const EXPERIMENT_METRIC_DESCRIPTORS: Record<
    ExperimentMetricKey,
    ExperimentMetricDescriptor
> = {
    conversion: {
        key: 'conversion',
        label: 'Form submission rate',
        kind: 'binary',
        hint: 'Low baseline — slowest to prove.',
        valueFormat: 'pct',
        value: (r) => r.conversion_rate,
        diffKind: 'rate-ci',
        diff: (r) => r.conversion_rate_diff,
        ciLow: (r) => r.conversion_rate_diff_ci_low,
        ciHigh: (r) => r.conversion_rate_diff_ci_high,
    },
    cta_click: {
        key: 'cta_click',
        // IX-3967: CTA-click funnel step (Rose + client CTAs pooled, one per
        // person). Stronger-intent step ABOVE the form-submit conversion — shown
        // as its own rate, never counted as a conversion.
        // IX-3990: now carries an always-valid (anytime-valid) lift CI — a Robbins
        // mixture confidence sequence computed in the RPC, peeking-safe under
        // continuous monitoring — so it reads as a comparison, not just counts.
        label: 'CTA click rate',
        kind: 'binary',
        hint: 'Higher up the funnel than form submit — higher baseline, an earlier read.',
        valueFormat: 'pct',
        value: (r) => r.cta_click_rate,
        diffKind: 'rate-ci',
        diff: (r) => r.cta_click_rate_diff,
        ciLow: (r) => r.cta_click_rate_diff_ci_low,
        ciHigh: (r) => r.cta_click_rate_diff_ci_high,
    },
    soft_conversion: {
        key: 'soft_conversion',
        label: 'Conversion incl. soft signals',
        kind: 'binary',
        hint: 'Form submit OR email captured — higher baseline than form-submit alone, an earlier read.',
        valueFormat: 'pct',
        value: (r) => r.soft_conversion_rate,
        diffKind: 'rate-ci',
        diff: (r) => r.soft_conversion_rate_diff,
        ciLow: (r) => r.soft_conversion_rate_diff_ci_low,
        ciHigh: (r) => r.soft_conversion_rate_diff_ci_high,
    },
    time_on_page: {
        key: 'time_on_page',
        label: 'Time on page',
        kind: 'continuous',
        hint: 'Continuous metric — usually the fastest to read.',
        valueFormat: 'duration',
        value: (r) => r.p50_duration_sec,
        diffKind: 'duration-pvalue',
        pValue: (r) => r.duration_mann_whitney_p_value,
        z: (r) => r.duration_mann_whitney_z,
    },
    bounce: {
        key: 'bounce',
        label: 'Bounce rate',
        kind: 'binary',
        invert: true,
        hint: 'Higher baseline than conversion — faster to read.',
        valueFormat: 'pct',
        value: (r) => r.bounce_rate,
        diffKind: 'rate-ci',
        diff: (r) => r.bounce_rate_diff,
        ciLow: (r) => r.bounce_rate_diff_ci_low,
        ciHigh: (r) => r.bounce_rate_diff_ci_high,
    },
    pricing: {
        key: 'pricing',
        label: 'Reached pricing',
        kind: 'binary',
        hint: 'Mid-funnel intent — much higher baseline, an early read.',
        valueFormat: 'pct',
        value: (r) => r.pricing_rate,
        diffKind: 'rate-ci',
        diff: (r) => r.pricing_rate_diff,
        ciLow: (r) => r.pricing_rate_diff_ci_low,
        ciHigh: (r) => r.pricing_rate_diff_ci_high,
    },
    /**
     * @deprecated Not rendered by any view. Diff is always NULL for on/off
     * holdouts (control never fires `widget_impression`). Use `bounce`. IX-3471.
     */
    bounce_exposed: {
        key: 'bounce_exposed',
        label: 'Bounce rate (after Rose seen)',
        kind: 'binary',
        invert: true,
        valueFormat: 'pct',
        value: (r) => r.bounce_exposed_rate,
        diffKind: 'rate-ci',
        diff: (r) => r.bounce_exposed_rate_diff,
        ciLow: (r) => r.bounce_exposed_rate_diff_ci_low,
        ciHigh: (r) => r.bounce_exposed_rate_diff_ci_high,
    },
    email_only: {
        key: 'email_only',
        label: 'Email captured (no form fill)',
        kind: 'binary',
        valueFormat: 'int',
        value: (r) => r.email_only_conversions,
        diffKind: 'informational',
    },
    session_p50: {
        key: 'session_p50',
        label: 'Typical session length (median)',
        kind: 'continuous',
        valueFormat: 'duration',
        value: (r) => r.p50_duration_sec,
        diffKind: 'duration-pvalue',
        pValue: (r) => r.duration_mann_whitney_p_value,
    },
    session_p75: {
        key: 'session_p75',
        label: 'Longer sessions (75th percentile)',
        kind: 'continuous',
        valueFormat: 'duration',
        value: (r) => r.p75_duration_sec,
        diffKind: 'informational',
    },
};

/**
 * Client readiness clocks, in display order.
 *
 * `pricing` ('Reached pricing') is intentionally omitted: its mart column
 * (`pricing_rate`) is parked NULL/0 by `analytics.refresh_experiment_results`
 * since IX-3534d (the per-pageview `url_matches_intent` probe was the refresh
 * bottleneck and was disabled). Re-add here once the precompute-pricing-patterns
 * CTE lands and the column is live again. The `pricing` descriptor is kept above
 * for type compatibility.
 */
export const CLIENT_CLOCK_KEYS: readonly MetricClockKey[] = [
    'conversion',
    'time_on_page',
    'bounce',
];

/** Client live-results table (running view), in display order. */
export const CLIENT_RUNNING_TABLE_KEYS: readonly ExperimentMetricKey[] = [
    'conversion',
    'cta_click',
    'soft_conversion',
    'bounce',
    'email_only',
];

/** Client completed-results table, in display order. */
export const CLIENT_COMPLETED_TABLE_KEYS: readonly ExperimentMetricKey[] = [
    'conversion',
    'soft_conversion',
    'email_only',
    'bounce',
    'session_p50',
    'session_p75',
];

/** Staff pooled comparison table, in display order. */
export const STAFF_TABLE_KEYS: readonly ExperimentMetricKey[] = [
    'conversion',
    'cta_click',
    'soft_conversion',
    // `pricing` omitted — column parked NULL/0 since IX-3534d. See CLIENT_CLOCK_KEYS note.
    'email_only',
    // `bounce` (whole-session) not `bounce_exposed` — the exposed variant's diff
    // is always NULL in an on/off holdout (control never sees Rose). IX-3471.
    'bounce',
    'session_p50',
    'session_p75',
];
